import { useQuery } from '@apollo/client';
import classNames from 'classnames';
import { isEmpty } from 'lodash';
import { FC, ReactNode, useMemo, useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import ShippingMethodSelect from '~/components/ProductVariants/ShippingMethodSelect';
import Text from '~/components/Text';
import { Identifiers } from '~/constants/enum';
import { QUERY_GET_CMS_BLOCKS } from '~/data/home';
import { QUERY_GET_ESTIMATE_SHIPPINGS, QUERY_SHIPPING_BY_COUNTRY } from '~/data/product/gql';
import { CountriesAtom, ShippingMethodByCountry } from '~/services/countries';
import { EstimateShippingAtom } from '~/services/estimate-shipping';
import styles from './ProductShipping.module.scss';
import StepShipping from './StepShipping';
import { Product } from '~/services/product';

interface ProductShippingProps {
    estimateShipping?: any[];
    returnsPolicy?: ReactNode;
    shipping?: ReactNode;
    shippingSteps?: any;
    product?: Product;
}

const ProductShipping: FC<ProductShippingProps> = ({
    estimateShipping,
    returnsPolicy,
    shipping,
    shippingSteps,
    product
}) => {
    const [showEst, setShowEst] = useState(false);
    const [changeShip, setChangeShip] = useState(false);
    const {
        shippingMethodSelected,
        countrySelected,
        shippingMethodByCountry,
        estimateShipping: _estimateShippingRecoil
    } = useRecoilValue(CountriesAtom.countriesSelector);
    const { progress } = useRecoilValue(EstimateShippingAtom.estimateShippingSelector);
    const setCountries = useSetRecoilState(CountriesAtom.countries);
    const setEstimateShipping = useSetRecoilState(EstimateShippingAtom.estimateShipping);

    const { data: dataCmsblock } = useQuery(QUERY_GET_CMS_BLOCKS, {
        variables: {
            identifiers: Identifiers.ProductRefund
        }
    });
    const { loading } = useQuery(QUERY_GET_ESTIMATE_SHIPPINGS, {
        variables: {
            product_sku: product?.sku,
            country_code: countrySelected?.value
        },
        onCompleted(data) {
            setEstimateShipping((prev) => ({
                ...prev,
                estimate: data?.EstimateShipping?.estimate_shipping
            }));
        },
        skip: !product?.sku || !countrySelected?.value
    });

    const { data: dataShippingByCountry } = useQuery(QUERY_SHIPPING_BY_COUNTRY, {
        variables: {
            country_code: countrySelected?.value
        },
        onCompleted(data) {
            const isFreeShip = data?.ShippingMethodByCountry?.find((sh: any) => !sh.shipping_cost);
            setCountries((prev) => ({
                ...prev,
                shippingMethodSelected:
                    prev?.shippingMethodSelected ?? isFreeShip ?? data?.ShippingMethodByCountry?.[0],
                shippingMethodByCountry: data?.ShippingMethodByCountry
            }));
        },
        skip: !countrySelected?.value
    });

    const _shippingByCountry: Array<ShippingMethodByCountry> = useMemo(() => {
        if (!dataShippingByCountry) return [];
        return dataShippingByCountry.ShippingMethodByCountry || [];
    }, [dataShippingByCountry]);

    const _estimateShipping = useMemo(() => {
        if (!progress) return [];

        const _estimateDelivery = progress;

        return _estimateDelivery
            ?.map((es) => {
                const shipping = _shippingByCountry.find((sh) => sh.carrier === es.carrier_code) || {};
                return {
                    ...es,
                    ...shipping
                };
            })
            .filter((es: any) => !!es?.title);
    }, [progress, _shippingByCountry]);

    const hanleSelectShippingMethod = (ship: any) => {
        const findShip = _shippingByCountry.find((sh) => sh.carrier === ship.carrier_code);
        if (!!findShip?.carrier) {
            setCountries((prev) => ({
                ...prev,
                shippingMethodSelected: findShip
            }));
        }
        setChangeShip(false);
        // setSelected(ship)
    };

    const currentEstimate = useMemo(() => {
        if (!isEmpty(_estimateShipping) && !isEmpty(shippingMethodSelected)) {
            const defaultShipping =
                _estimateShipping?.filter((i) => i.carrier_code === 'freeshipping')?.[0] ??
                _estimateShipping?.[0] ??
                ({} as any);
            const shipping = _estimateShipping?.find((es) => es.carrier_code === shippingMethodSelected?.carrier);

            return shipping ?? defaultShipping;
        }

        return {} as any;
    }, [shippingMethodSelected?.carrier, _estimateShipping]);

    if (isEmpty(currentEstimate)) return null;
    return (
        <div className="flex flex-col gap-2">
            <div className="flex gap-2 lg:gap-2 flex-wrap">
                <ShippingMethodSelect />
            </div>
            <div className={classNames('flex gap-2')}>
                <Text type="body-2">Order now to get by</Text>
                <Text onClick={() => setShowEst(!showEst)} type="body-2" weight={600} className={styles.orderNow}>
                    {currentEstimate?.shipsDeliveredFrom?.format('MMM DD') || ''}
                    {'-'}
                    {currentEstimate?.shipsDeliveredTo?.format('MMM DD') || ''}
                </Text>
            </div>
            <div className="flex gap-2  relative">
                <Text type="body-2">Shipping:</Text>
                <div className="flex relative">
                    <Text
                        type="title-5"
                        className="underline cursor-pointer "
                        onClick={() => setChangeShip(!changeShip)}
                    >
                        {currentEstimate?.title || ''}
                    </Text>
                    <div
                        className={classNames('flex flex-col ', styles.dropdown, {
                            [styles.openDrowpdown]: !!changeShip
                        })}
                    >
                        {!!_estimateShipping.length
                            ? _estimateShipping?.map((es: any, index) => (
                                  <div
                                      key={es.carrier_code}
                                      className={classNames(styles.dropdownItem, {
                                          [styles.selected]: es?.carrier_code === shippingMethodSelected?.carrier
                                      })}
                                      onClick={() => {
                                          hanleSelectShippingMethod(es);
                                      }}
                                  >
                                      {es.title}
                                  </div>
                              ))
                            : null}
                    </div>
                </div>
            </div>
            <div className="inline-flex max-w-[400px]">
                {currentEstimate?.steps && <StepShipping steps={currentEstimate?.steps} />}
            </div>
            <div>
                <div
                    dangerouslySetInnerHTML={{
                        __html: dataCmsblock?.cmsBlocks?.items?.[0].content
                    }}
                />
            </div>
        </div>
    );
};

export default ProductShipping;
